import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import type {
  AdminLessonDetailDto,
  AdminMembersResponseDto,
  AdminRecurringSlotDto,
  CreateRecurringSlotRequestDto,
  MemberRole,
  RecurrencePreset,
  RecurringSlotStatus,
  StudiosResponseDto,
  StudioDecisionStatus,
  TeacherAssignmentStatus
} from "@madamuh/domain";
import { getRecurrencePresetLabel } from "@madamuh/domain";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchAdminMembers, fetchAdminStudios } from "../../shared/api/adminDirectory";
import {
  createAdminRecurringSlotForLesson,
  fetchAdminLesson,
  fetchAdminRecurringSlot,
  patchAdminRecurringSlot,
  transitionAdminRecurringSlot
} from "../../shared/api/adminLesson";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { describeStatus } from "../../shared/lib/uiLabels";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const WEEKDAY_OPTIONS = [
  { value: 0, label: "日" },
  { value: 1, label: "月" },
  { value: 2, label: "火" },
  { value: 3, label: "水" },
  { value: 4, label: "木" },
  { value: 5, label: "金" },
  { value: 6, label: "土" }
] as const;

const RECURRENCE_OPTIONS: Exclude<RecurrencePreset, "irregular">[] = [
  "weekly",
  "monthlyWeeks1To4",
  "monthlyWeeks1And3",
  "monthlyWeeks2And4",
  "monthlyWeekOfStartDate"
];

const STATUS_OPTIONS: RecurringSlotStatus[] = ["active", "paused", "ended"];

type SlotFormState = {
  weekday: string;
  recurrencePreset: Exclude<RecurrencePreset, "irregular">;
  startTime: string;
  endTime: string;
  studioId: string;
  studioDecisionStatus: StudioDecisionStatus;
  teacherPlatformUserIds: string[];
  teacherAssignmentStatus: TeacherAssignmentStatus;
  activeFrom: string;
  activeTo: string;
  status: RecurringSlotStatus;
};

type LocationState = {
  feedbackMessage?: string;
} | null;

function hasTeacherRole(roles: readonly MemberRole[]): boolean {
  return roles.includes("teacher") || roles.includes("admin");
}

function getMemberDisplayName(member: AdminMembersResponseDto["items"][number]): string {
  return member.appNickname ?? member.lineDisplayNameSnapshot ?? member.platformUserId;
}

function toggleSelection(current: readonly string[], value: string, checked: boolean): string[] {
  const next = new Set(current);

  if (checked) {
    next.add(value);
  } else {
    next.delete(value);
  }

  return [...next];
}

function createInitialSlotFormState(
  lesson: AdminLessonDetailDto,
  slot?: AdminRecurringSlotDto | null
): SlotFormState {
  const fallbackSlot = slot ?? lesson.recurringSlots[0] ?? null;
  const recurrencePreset = (fallbackSlot?.recurrencePreset && fallbackSlot.recurrencePreset !== "irregular"
    ? fallbackSlot.recurrencePreset
    : "weekly") as Exclude<RecurrencePreset, "irregular">;

  return {
    weekday: String(fallbackSlot?.weekday ?? 0),
    recurrencePreset,
    startTime: fallbackSlot?.startTime ?? lesson.defaultStartTime ?? "17:00",
    endTime: fallbackSlot?.endTime ?? lesson.defaultEndTime ?? "18:00",
    studioId: fallbackSlot?.studioId ?? lesson.defaultStudioId ?? "",
    studioDecisionStatus:
      fallbackSlot?.studioDecisionStatus === "confirmed" || fallbackSlot?.studioDecisionStatus === "tbd"
        ? fallbackSlot.studioDecisionStatus
        : lesson.defaultStudioDecisionStatus ?? "tbd",
    teacherPlatformUserIds:
      fallbackSlot?.defaultTeacherPlatformUserIds.length
        ? [...fallbackSlot.defaultTeacherPlatformUserIds]
        : [...lesson.defaultTeacherPlatformUserIds],
    teacherAssignmentStatus:
      fallbackSlot?.teacherAssignmentStatus === "assigned" || fallbackSlot?.teacherAssignmentStatus === "tbd"
        ? fallbackSlot.teacherAssignmentStatus
        : lesson.defaultTeacherAssignmentStatus ?? "tbd",
    activeFrom: fallbackSlot?.activeFrom ?? lesson.startDate,
    activeTo:
      fallbackSlot?.activeTo && fallbackSlot.activeTo !== "9999-12-31"
        ? fallbackSlot.activeTo
        : lesson.endDate !== "9999-12-31"
          ? lesson.endDate
          : "",
    status: fallbackSlot?.status ?? "active"
  };
}

export function AdminRecurringSlotDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantSlug = "unknown-tenant", lessonId = "", slotId } = useParams();
  const isCreateMode = slotId === undefined;
  const locationState = location.state as LocationState;
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [lesson, setLesson] = useState<AdminLessonDetailDto | null>(null);
  const [slot, setSlot] = useState<AdminRecurringSlotDto | null>(null);
  const [members, setMembers] = useState<AdminMembersResponseDto | null>(null);
  const [studios, setStudios] = useState<StudiosResponseDto | null>(null);
  const [formState, setFormState] = useState<SlotFormState | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(locationState?.feedbackMessage ?? null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const teacherMembers = useMemo(
    () => members?.items.filter((member) => hasTeacherRole(member.roles)) ?? [],
    [members]
  );

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    let cancelled = false;

    async function loadPage() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const [nextLesson, nextMembers, nextStudios, nextSlot] = await Promise.all([
          fetchAdminLesson(tenantSlug, lessonId),
          fetchAdminMembers(tenantSlug),
          fetchAdminStudios(tenantSlug),
          isCreateMode || !slotId ? Promise.resolve(null) : fetchAdminRecurringSlot(tenantSlug, slotId)
        ]);

        if (cancelled) {
          return;
        }

        if (nextSlot && nextSlot.lessonId !== nextLesson.lessonId) {
          setLoadState("error");
          setLoadError("指定した定期枠がこのレッスンに属していません。");
          return;
        }

        setLesson(nextLesson);
        setMembers(nextMembers);
        setStudios(nextStudios);
        setSlot(nextSlot);
        setFormState(createInitialSlotFormState(nextLesson, nextSlot));
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "定期枠の取得に失敗しました"));
      }
    }

    void loadPage();

    return () => {
      cancelled = true;
    };
  }, [hasAdminRole, isCreateMode, isSessionReady, lessonId, navigate, slotId, tenantSlug]);

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!lesson || !formState) {
      return;
    }

    setBusyKey("save-slot");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const request = {
        weekday: Number.parseInt(formState.weekday, 10),
        activeFrom: formState.activeFrom,
        activeTo: formState.activeTo.trim() || null,
        startTime: formState.startTime,
        endTime: formState.endTime,
        recurrencePreset: formState.recurrencePreset,
        studioId: formState.studioDecisionStatus === "tbd" ? null : formState.studioId || null,
        studioDecisionStatus: formState.studioDecisionStatus,
        studioTbd: formState.studioDecisionStatus === "tbd",
        defaultTeacherPlatformUserIds:
          formState.teacherAssignmentStatus === "tbd" ? [] : [...formState.teacherPlatformUserIds],
        teacherAssignmentStatus: formState.teacherAssignmentStatus,
        teacherTbd: formState.teacherAssignmentStatus === "tbd",
        status: formState.status
      } satisfies Omit<CreateRecurringSlotRequestDto, "lessonId"> & { lessonId?: string };

      if (isCreateMode) {
        const createdSlot = await createAdminRecurringSlotForLesson(tenantSlug, lesson.lessonId, request);
        navigate(
          `/t/${tenantSlug}/teacher/admin/lessons/${lesson.lessonId}/recurring-slots/${createdSlot.slotId}`,
          {
            replace: true,
            state: {
              feedbackMessage: "定期枠を追加しました。"
            } satisfies LocationState
          }
        );
        return;
      }

      if (!slotId || !slot) {
        return;
      }

      const updatedSlot = await patchAdminRecurringSlot(tenantSlug, slotId, request);

      if (slot.status !== formState.status) {
        await transitionAdminRecurringSlot(tenantSlug, slotId, { toStatus: formState.status });
      }

      const refreshedSlot = await fetchAdminRecurringSlot(tenantSlug, slotId);
      setSlot(refreshedSlot);
      setFormState(createInitialSlotFormState(lesson, refreshedSlot));
      setFeedbackMessage("定期枠を更新しました。");

      if (updatedSlot.lessonId !== lesson.lessonId) {
        setLoadError("更新後の定期枠とレッスンの対応に不整合があります。");
      }
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "定期枠の保存に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title={isCreateMode ? "定期枠を追加" : "定期枠詳細"}
      description={
        isCreateMode
          ? "通常レッスンに対して 1 つの定期枠を追加します。"
          : "1 つの定期枠を編集します。曜日は単体で選択します。"
      }
      links={[
        { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
        { to: "/t/:tenantSlug/teacher/admin/lessons", label: "レッスン一覧" }
      ]}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>定期枠を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}

        {lesson ? (
          <section style={{ display: "grid", gap: 6, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}>
            <strong>{lesson.title}</strong>
            <div style={{ fontSize: 14, opacity: 0.8 }}>
              {describeStatus(lesson.lessonKind)} / {describeStatus(lesson.status)} / {lesson.startDate} 〜 {lesson.endDate}
            </div>
          </section>
        ) : null}

        {lesson && formState ? (
          <form
            onSubmit={(event) => void handleSave(event)}
            style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
          >
            {!isCreateMode && slot ? (
              <div style={{ display: "grid", gap: 4, fontSize: 13, opacity: 0.8 }}>
                <span>定期枠ID: {slot.slotId}</span>
                <span>作成日時: {slot.createdAt}</span>
                <span>更新日時: {slot.updatedAt}</span>
              </div>
            ) : null}

            <label style={{ display: "grid", gap: 4 }}>
              <span>繰り返し</span>
              <select
                value={formState.recurrencePreset}
                onChange={(event) =>
                  setFormState((current) =>
                    current
                      ? {
                          ...current,
                          recurrencePreset: event.target.value as Exclude<RecurrencePreset, "irregular">
                        }
                      : current
                  )
                }
              >
                {RECURRENCE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {getRecurrencePresetLabel(option)}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "grid", gap: 4 }}>
              <span>曜日</span>
              <select
                value={formState.weekday}
                onChange={(event) =>
                  setFormState((current) => current ? { ...current, weekday: event.target.value } : current)
                }
              >
                {WEEKDAY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
              <label style={{ display: "grid", gap: 4 }}>
                <span>開始時間</span>
                <input
                  type="time"
                  required
                  value={formState.startTime}
                  onChange={(event) =>
                    setFormState((current) => current ? { ...current, startTime: event.target.value } : current)
                  }
                />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>終了時間</span>
                <input
                  type="time"
                  required
                  value={formState.endTime}
                  onChange={(event) =>
                    setFormState((current) => current ? { ...current, endTime: event.target.value } : current)
                  }
                />
              </label>
            </div>

            <label style={{ display: "grid", gap: 4 }}>
              <span>会場</span>
              <select
                value={formState.studioId}
                disabled={formState.studioDecisionStatus === "tbd"}
                onChange={(event) =>
                  setFormState((current) => current ? { ...current, studioId: event.target.value } : current)
                }
              >
                <option value="">選択してください</option>
                {studios?.items.map((studio) => (
                  <option key={studio.studioId} value={studio.studioId}>
                    {studio.displayName}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={formState.studioDecisionStatus === "tbd"}
                onChange={(event) =>
                  setFormState((current) =>
                    current
                      ? {
                          ...current,
                          studioDecisionStatus: event.target.checked ? "tbd" : "confirmed",
                          studioId: event.target.checked ? "" : current.studioId
                        }
                      : current
                  )
                }
              />
              <span>会場未定</span>
            </label>

            <fieldset style={{ display: "grid", gap: 8, border: "none", padding: 0, margin: 0 }}>
              <legend style={{ padding: 0 }}>講師</legend>
              <div style={{ display: "grid", gap: 8 }}>
                {teacherMembers.map((member) => (
                  <label key={member.platformUserId} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={formState.teacherPlatformUserIds.includes(member.platformUserId)}
                      disabled={formState.teacherAssignmentStatus === "tbd"}
                      onChange={(event) =>
                        setFormState((current) =>
                          current
                            ? {
                                ...current,
                                teacherPlatformUserIds: toggleSelection(
                                  current.teacherPlatformUserIds,
                                  member.platformUserId,
                                  event.target.checked
                                )
                              }
                            : current
                        )
                      }
                    />
                    <span>{getMemberDisplayName(member)}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={formState.teacherAssignmentStatus === "tbd"}
                onChange={(event) =>
                  setFormState((current) =>
                    current
                      ? {
                          ...current,
                          teacherAssignmentStatus: event.target.checked ? "tbd" : "assigned",
                          teacherPlatformUserIds: event.target.checked ? [] : current.teacherPlatformUserIds
                        }
                      : current
                  )
                }
              />
              <span>講師未定</span>
            </label>

            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
              <label style={{ display: "grid", gap: 4 }}>
                <span>有効開始日</span>
                <input
                  type="date"
                  required
                  value={formState.activeFrom}
                  onChange={(event) =>
                    setFormState((current) => current ? { ...current, activeFrom: event.target.value } : current)
                  }
                />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>有効終了日</span>
                <input
                  type="date"
                  value={formState.activeTo}
                  onChange={(event) =>
                    setFormState((current) => current ? { ...current, activeTo: event.target.value } : current)
                  }
                />
              </label>
            </div>

            <label style={{ display: "grid", gap: 4 }}>
              <span>ステータス</span>
              <select
                value={formState.status}
                onChange={(event) =>
                  setFormState((current) =>
                    current ? { ...current, status: event.target.value as RecurringSlotStatus } : current
                  )
                }
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {describeStatus(status)}
                  </option>
                ))}
              </select>
            </label>

            <button type="submit" disabled={busyKey === "save-slot"}>
              {busyKey === "save-slot" ? "保存中..." : isCreateMode ? "定期枠を追加する" : "定期枠を保存する"}
            </button>

            <div style={{ display: "grid", gap: 4, fontSize: 12, opacity: 0.8 }}>
              <span>定期枠を無効化すると、今後 Generate したときにその枠の予定は作成されません。</span>
              <span>定期枠を変更しました。反映するには対象月のスケジュールを再生成してください。</span>
              <span>反映後に確認・公開するには、対象月で再生成・確認・公開を行ってください。</span>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Link to={`/t/${tenantSlug}/teacher/admin/lessons/${lesson.lessonId}`}>レッスン詳細へ戻る</Link>
            </div>
          </form>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
